import {
  differenceInCalendarDays,
  differenceInMinutes,
  format,
  formatDistance,
  formatRelative,
  isSameWeek,
  isSameYear,
} from 'date-fns';

import type { Locale } from 'date-fns';

type FormatRelativeDateRangeOptions = {
  /** date-fns locale object */
  locale?: Locale;
  /** Reference date to compare against (defaults to now) */
  referenceDate?: Date;
};

/**
 * Smart relative date range formatting based on time distance
 *
 * Rules:
 * - If starts in ≤ 60 min → "in X minutes, HH:mm – HH:mm" / "через X хвилин, HH:mm – HH:mm"
 * - If starts today but > 60 min → "today, HH:mm – HH:mm" / "сьогодні, HH:mm – HH:mm"
 * - If yesterday → "yesterday, HH:mm – HH:mm" / "вчора, HH:mm – HH:mm"
 * - If tomorrow → "tomorrow, HH:mm – HH:mm" / "завтра, HH:mm – HH:mm"
 * - If in the same week → "weekday, HH:mm – HH:mm" / "день тижня, HH:mm – HH:mm"
 * - If date in current year → "DD MMM, HH:mm – HH:mm" (21 Nov, 14:30 – 15:30)
 * - If date not in current year → "DD.MM.YYYY, HH:mm – HH:mm" (21.11.2026, 14:30 – 15:30)
 *
 * @param startDate - Start of range (Date object or ISO string)
 * @param endDate - End of range (Date object or ISO string)
 * @param options - Formatting options
 * @param options.locale - date-fns locale object
 * @param options.referenceDate - Reference date to compare against (defaults to now)
 *
 * @returns Formatted date range string
 *
 * @example
 * ```ts
 * import { uk } from 'date-fns/locale'
 *
 * // In 30 minutes
 * const start1 = new Date(Date.now() + 30 * 60 * 1000)
 * const end1 = new Date(Date.now() + 90 * 60 * 1000)
 * formatRelativeDateRange(start1, end1, { locale: uk })
 * // => "через 30 хвилин, 14:30 – 15:30"
 *
 * // In 90 minutes (today)
 * const start2 = new Date(Date.now() + 90 * 60 * 1000)
 * const end2 = new Date(Date.now() + 150 * 60 * 1000)
 * formatRelativeDateRange(start2, end2, { locale: uk })
 * // => "сьогодні, 15:30 – 16:30"
 *
 * // Tomorrow
 * const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000)
 * const tomorrowEnd = new Date(Date.now() + 25 * 60 * 60 * 1000)
 * formatRelativeDateRange(tomorrow, tomorrowEnd, { locale: uk })
 * // => "завтра, 14:30 – 15:30"
 *
 * // Same week (Tuesday)
 * const tuesday = new Date('2025-01-07T14:30:00')
 * const tuesdayEnd = new Date('2025-01-07T15:30:00')
 * formatRelativeDateRange(tuesday, tuesdayEnd, { locale: uk })
 * // => "вівторок, 14:30 – 15:30"
 *
 * // Same year (different week)
 * const sameYear = new Date('2025-11-21T14:30:00')
 * const sameYearEnd = new Date('2025-11-21T15:30:00')
 * formatRelativeDateRange(sameYear, sameYearEnd, { locale: uk })
 * // => "21 лис, 14:30 – 15:30"
 *
 * // Different year
 * const differentYear = new Date('2026-11-21T14:30:00')
 * const differentYearEnd = new Date('2026-11-21T15:30:00')
 * formatRelativeDateRange(differentYear, differentYearEnd, { locale: uk })
 * // => "21.11.2026, 14:30 – 15:30"
 * ```
 */
export function formatRelativeDateRange(
  startDate: Date | string,
  endDate: Date | string,
  { locale, referenceDate = new Date() }: FormatRelativeDateRangeOptions = {},
): string {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

  const timeRange = `${format(start, 'HH:mm', { locale })} – ${format(end, 'HH:mm', { locale })}`;
  const minutesDiff = differenceInMinutes(start, referenceDate);

  // Rule 1: If starts in ≤ 60 min → use formatDistance
  if (minutesDiff > 0 && minutesDiff <= 60) {
    const relativeTime = formatDistance(start, referenceDate, { addSuffix: true, locale });

    return `${relativeTime}, ${timeRange}`;
  }

  // Rule 2-4: If today, yesterday, or tomorrow
  const daysDiff = differenceInCalendarDays(start, referenceDate);
  const absDaysDiff = Math.abs(daysDiff);

  if (absDaysDiff <= 1) {
    // formatRelative returns "today at HH:mm", we need just the day part
    const relativeDay = formatRelative(start, referenceDate, { locale });
    // Remove the time part that formatRelative adds (everything after "at" or "о")
    // eslint-disable-next-line sonarjs/slow-regex
    const dayPart = relativeDay.replace(/\s+(at|о)\s+\d{1,2}:\d{2}.*$/i, '');

    return `${dayPart}, ${timeRange}`;
  }

  // Rule 5: If in the same week (2-6 days) → show weekday name
  if (isSameWeek(start, referenceDate, { locale }) && absDaysDiff >= 2 && absDaysDiff <= 6) {
    const weekday = format(start, 'EEEE', { locale });

    return `${weekday}, ${timeRange}`;
  }

  // Rule 6: If date in current year → "DD MMM, HH:mm – HH:mm"
  if (isSameYear(start, referenceDate)) {
    const datePart = format(start, 'd MMM', { locale });

    return `${datePart}, ${timeRange}`;
  }

  // Rule 7: If date not in current year → "DD.MM.YYYY, HH:mm – HH:mm"
  const datePart = format(start, 'dd.MM.yyyy', { locale });

  return `${datePart}, ${timeRange}`;
}
